import { useState, useEffect } from 'preact/hooks';
import { useFilter } from '../../contexts/FilterContext';
import { dataService } from '../../functions/apis/dataService';
import type { CashflowStatement } from '../../types/api';
import type { SankeyLink, SankeyNode } from '../../components/charts/SankeyChart';
import type { MonthOption, MonthRangeValue } from '../../components/charts/MonthRangeSlider';
import {
  buildSankeyFromCashflow,
  extractAvailableMonthsAsc,
  filterCashflowByRange,
} from './cashflow.utils';

export function useUnitSankey() {
  const { selectedClient, selectedUnit } = useFilter();
  const [loading, setLoading] = useState(false);
  const [cashflowData, setCashflowData] = useState<CashflowStatement[] | null>(null);
  const [monthsAsc, setMonthsAsc] = useState<MonthOption[]>([]);
  const [range, setRange] = useState<MonthRangeValue>({ startId: '', endId: '' });
  const [nodes, setNodes] = useState<SankeyNode[]>([]);
  const [links, setLinks] = useState<SankeyLink[]>([]);

  useEffect(() => {
    if (!selectedClient?.user_id_no || !selectedUnit?.unit_id_no) {
      setCashflowData(null);
      setMonthsAsc([]);
      setRange({ startId: '', endId: '' });
      setNodes([]);
      setLinks([]);
      return;
    }

    setLoading(true);
    dataService
      .getCashflow({
        user_id_no: selectedClient.user_id_no,
        unit_id_no: selectedUnit.unit_id_no,
      })
      .then((data) => {
        setCashflowData(data);
        const months = extractAvailableMonthsAsc(data);
        setMonthsAsc(months);
        setRange(
          months.length > 0
            ? { startId: months[0].id, endId: months[months.length - 1].id }
            : { startId: '', endId: '' }
        );
      })
      .catch((error) => {
        console.error('Error fetching sankey chart data:', error);
        setCashflowData(null);
        setMonthsAsc([]);
        setRange({ startId: '', endId: '' });
      })
      .finally(() => setLoading(false));
  }, [selectedClient, selectedUnit]);

  useEffect(() => {
    if (!cashflowData || !range.startId || !range.endId) {
      setNodes([]);
      setLinks([]);
      return;
    }

    const filtered = filterCashflowByRange(cashflowData, range.startId, range.endId);
    const result = buildSankeyFromCashflow(filtered);
    setNodes(result.nodes);
    setLinks(result.links);
  }, [cashflowData, range]);

  return {
    loading,
    monthsAsc,
    range,
    nodes,
    links,
    setRange,
  };
}
